import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { DialogProps } from '@mui/material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import type { ConfirmOptions } from 'src/components/app/context/ConfirmContext';
import ModalCloseHeader from 'src/components/common/Modal/ModalCloseHeader';

export interface ConfirmDialogProps extends DialogProps {
  onClose: () => void;
  onPrimary: () => void;
  onSecondary: () => void;
  options: ConfirmOptions;
}

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  onClose,
  onPrimary,
  onSecondary,
  options,
  ...props
}) => {
  const {
    content,
    description,
    dialogProps,
    primaryButtonProps,
    primaryText,
    secondaryButtonProps,
    secondaryText,
    title,
  } = options;
  const { t } = useTranslation();

  return (
    <Dialog
      data-test-id="confirm-dialog"
      fullWidth
      onClose={onClose}
      {...props}
      {...dialogProps}
    >
      <ModalCloseHeader onClose={onClose} />

      <DialogTitle component="div">
        {!!title && <Typography variant="h4">{t(title)}</Typography>}
        {!!description && (
          <Typography variant="h6">{t(description)}</Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <>
          {!!content && { content }}
          {(!!secondaryText || !!primaryText) && (
            <DialogActions>
              {!!secondaryText && (
                <Button
                  variant="cta"
                  color="secondary"
                  fullWidth
                  onClick={onSecondary}
                  {...secondaryButtonProps}
                  data-test-id="confirm-dialog-cancel-button"
                >
                  {t(secondaryText)}
                </Button>
              )}

              {!!primaryText && (
                <Button
                  variant="cta"
                  autoFocus
                  fullWidth
                  onClick={onPrimary}
                  {...primaryButtonProps}
                  data-test-id="confirm-dialog-accept-button"
                >
                  {t(primaryText)}
                </Button>
              )}
            </DialogActions>
          )}
        </>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
